import { useEffect, useMemo, useState } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { X, Scales, Percent, Barbell } from "@phosphor-icons/react";
import SmartDateInput from "@/components/SmartDateInput";

// Circumference sequence exactly as requested by user (Italian nutritionist standard).
// Order matches the highlighted Excel image, left→right:
//   Avambraccio, Braccio, (Torace — M only), Giro vita, Circ. periombellicale,
//   (Circ. glutei — F only), Giro coscia, Circ. vasto mediale, Polpaccio.
const CIRC_TOP = [
  { key: "avambraccio", label: "Avambraccio", hint: "punto più largo" },
  { key: "braccio", label: "Braccio", hint: "punto più largo" },
];
const TORACE_FIELD = { key: "torace", label: "Torace", hint: "punto più largo" };
const CIRC_MID = [
  { key: "giro_vita", label: "Giro vita", hint: "punto più stretto" },
  { key: "periombelicale", label: "Circ. periombellicale", hint: "" },
];
const CIRC_AFTER_HIPS = [
  { key: "giro_coscia", label: "Giro coscia", hint: "punto più largo" },
  { key: "vasto_mediale", label: "Circ. vasto mediale", hint: "" },
  { key: "polpaccio", label: "Polpaccio", hint: "" },
];
const GLUTEI_FIELD = { key: "glutei", label: "Circ. glutei", hint: "punto più largo" };

function toNumber(v) {
  if (v === "" || v == null) return 0;
  const s = String(v).replace(",", ".");
  const n = parseFloat(s);
  return Number.isFinite(n) ? n : 0;
}

function sanitizeDecimal(v) {
  if (typeof v !== "string") return v;
  let s = v.replace(/[^\d.,]/g, "");
  const firstSep = s.search(/[.,]/);
  if (firstSep !== -1) {
    const rest = s.slice(firstSep + 1).replace(/[.,]/g, "");
    s = s.slice(0, firstSep + 1) + rest;
  }
  return s;
}

function NumField({ testId, label, hint, suffix, value, onChange }) {
  return (
    <div>
      <div className="min-h-[34px]">
        <Label className="label-tiny block">{label}</Label>
        {hint ? <div className="text-[10px] text-[color:var(--text-secondary)] italic mt-0.5">{hint}</div> : null}
      </div>
      <div className="relative mt-1">
        <Input
          data-testid={testId}
          type="text"
          inputMode="decimal"
          autoComplete="off"
          value={value}
          onChange={(e) => onChange(sanitizeDecimal(e.target.value))}
          onWheel={(e) => e.currentTarget.blur()}
          className="pr-10 tabular-nums"
        />
        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-[color:var(--text-secondary)]">{suffix}</span>
      </div>
    </div>
  );
}

const emptyForm = (today) => ({
  date: today, peso_kg: "", bodyfat_pct: "",
  avambraccio: "", braccio: "", torace: "", glutei: "",
  giro_vita: "", periombelicale: "", giro_coscia: "",
  vasto_mediale: "", polpaccio: "", notes: "",
});

const numToStr = (v) => (v === 0 || v == null || v === "") ? "" : String(v).replace(".", ",");

export default function MeasurementForm({ gender = "M", initial = null, onSave, onCancel }) {
  const today = new Date().toISOString().slice(0, 10);
  const [form, setForm] = useState(emptyForm(today));
  const editing = !!initial;

  useEffect(() => {
    if (initial) {
      setForm({
        date: initial.date || today,
        peso_kg: numToStr(initial.peso_kg),
        bodyfat_pct: numToStr(initial.bodyfat_pct ? initial.bodyfat_pct * 100 : ""),
        avambraccio: numToStr(initial.avambraccio),
        braccio: numToStr(initial.braccio),
        torace: numToStr(initial.torace),
        glutei: numToStr(initial.glutei),
        giro_vita: numToStr(initial.giro_vita),
        periombelicale: numToStr(initial.periombelicale),
        giro_coscia: numToStr(initial.giro_coscia),
        vasto_mediale: numToStr(initial.vasto_mediale),
        polpaccio: numToStr(initial.polpaccio),
        notes: initial.notes || "",
      });
    } else {
      setForm(emptyForm(today));
    }
    // eslint-disable-next-line
  }, [initial?.measurement_id]);

  const setV = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  // LIVE massa magra = peso - (peso * bodyfat%/100). Displayed with 1 decimal.
  const massaMagra = useMemo(() => {
    const peso = toNumber(form.peso_kg);
    const bfInput = toNumber(form.bodyfat_pct);
    if (peso <= 0 || bfInput <= 0) return null;
    const bf = bfInput > 1 ? bfInput / 100 : bfInput;
    return Math.round((peso - peso * bf) * 10) / 10;
  }, [form.peso_kg, form.bodyfat_pct]);

  const submit = async (e) => {
    e.preventDefault();
    const payload = { notes: form.notes };
    if (!editing) payload.date = form.date;
    for (const k of Object.keys(form)) {
      if (k === "date" || k === "notes") continue;
      payload[k] = toNumber(form[k]);
    }
    if (payload.bodyfat_pct > 1) payload.bodyfat_pct = payload.bodyfat_pct / 100;
    await onSave(payload);
    if (!editing) setForm(emptyForm(today));
  };

  return (
    <form data-testid="measurement-form" onSubmit={submit} className="space-y-5 border border-[color:var(--border)] rounded-2xl p-5 bg-[color:var(--bg-primary)]">
      <div className="flex items-center justify-between">
        <div className="label-tiny">{editing ? "Modifica rilievo" : "Nuovo rilievo"}</div>
        <button
          type="button"
          data-testid="cancel-edit-measurement-btn"
          onClick={onCancel}
          className="text-xs inline-flex items-center gap-1 text-[color:var(--text-secondary)] hover:text-[color:var(--text-primary)]"
        >
          <X size={12} weight="bold" /> {editing ? "Annulla" : "Chiudi"}
        </button>
      </div>

      <div>
        <Label className="label-tiny">Data rilievo</Label>
        <SmartDateInput
          data-testid="m-date"
          value={form.date}
          onChange={(iso) => setV("date", iso)}
          disabled={editing}
          required
        />
        {editing && <div className="text-[10px] text-[color:var(--text-secondary)] mt-1 uppercase tracking-widest">Data originale conservata</div>}
      </div>

      {/* Prominent composition block — 3 aligned cards, uniform font with circumferences */}
      <div>
        <div className="label-tiny mb-2">Composizione corporea</div>
        <div className="grid grid-cols-3 gap-2 rounded-xl border border-[color:var(--border)] p-2" style={{ background: "var(--bg-secondary)" }}>
          {[
            { key: "peso_kg", label: "Peso", suffix: "kg", icon: Scales, accent: "var(--accent)" },
            { key: "bodyfat_pct", label: "Body fat", suffix: "%", icon: Percent, accent: "var(--accent)" },
          ].map((c) => {
            const Icon = c.icon;
            return (
              <div key={c.key} className="rounded-lg p-2 flex flex-col min-w-0" style={{ background: "var(--bg-primary)" }}>
                <div className="h-[42px] flex flex-col justify-start min-w-0">
                  <div className="flex items-center gap-1 label-tiny truncate">
                    <Icon size={12} weight="duotone" style={{ color: c.accent, flexShrink: 0 }} />
                    <span className="truncate">{c.label}</span>
                  </div>
                  <div className="text-[10px] text-[color:var(--text-secondary)] italic mt-0.5 whitespace-nowrap">&nbsp;</div>
                </div>
                <div className="relative mt-auto min-w-0">
                  <Input
                    data-testid={`m-${c.key}`}
                    type="text"
                    inputMode="decimal"
                    autoComplete="off"
                    value={form[c.key]}
                    onChange={(e) => setV(c.key, sanitizeDecimal(e.target.value))}
                    onWheel={(e) => e.currentTarget.blur()}
                    className="pr-8 pl-2 tabular-nums w-full"
                  />
                  <span className="absolute right-2 top-1/2 -translate-y-1/2 text-[10px] text-[color:var(--text-secondary)]">{c.suffix}</span>
                </div>
              </div>
            );
          })}
          <div className="rounded-lg p-2 flex flex-col min-w-0" style={{ background: "var(--bg-primary)" }}>
            <div className="h-[42px] flex flex-col justify-start min-w-0">
              <div className="flex items-center gap-1 label-tiny truncate">
                <Barbell size={12} weight="duotone" style={{ color: "var(--accent-2)", flexShrink: 0 }} />
                <span className="truncate">M. magra</span>
              </div>
              <div className="text-[10px] text-[color:var(--text-secondary)] italic mt-0.5 whitespace-nowrap">calcolo auto.</div>
            </div>
            <div
              className="mt-auto h-9 rounded-md border border-dashed border-[color:var(--border)] pl-2 pr-2 flex items-center justify-between text-sm tabular-nums min-w-0"
              data-testid="m-massa_magra"
            >
              <span className="truncate">
                {massaMagra != null ? String(massaMagra).replace(".", ",") : "—"}
              </span>
              <span className="text-[10px] text-[color:var(--text-secondary)] flex-shrink-0 ml-1">kg</span>
            </div>
          </div>
        </div>
      </div>

      {/* Circumferences — strict sequence from Excel image */}
      <div>
        <div className="label-tiny mb-2">Circonferenze <span className="normal-case text-[color:var(--text-secondary)] tracking-normal">— in cm</span></div>
        <div className="grid grid-cols-2 gap-x-3 gap-y-4">
          {CIRC_TOP.map((f) => (
            <NumField key={f.key} testId={`m-${f.key}`} label={f.label} hint={f.hint} suffix="cm" value={form[f.key]} onChange={(v) => setV(f.key, v)} />
          ))}
          {gender === "M" && (
            <NumField testId="m-torace" label={TORACE_FIELD.label} hint={TORACE_FIELD.hint} suffix="cm" value={form.torace} onChange={(v) => setV("torace", v)} />
          )}
          {CIRC_MID.map((f) => (
            <NumField key={f.key} testId={`m-${f.key}`} label={f.label} hint={f.hint} suffix="cm" value={form[f.key]} onChange={(v) => setV(f.key, v)} />
          ))}
          {gender === "F" && (
            <NumField testId="m-glutei" label={GLUTEI_FIELD.label} hint={GLUTEI_FIELD.hint} suffix="cm" value={form.glutei} onChange={(v) => setV("glutei", v)} />
          )}
          {CIRC_AFTER_HIPS.map((f) => (
            <NumField key={f.key} testId={`m-${f.key}`} label={f.label} hint={f.hint} suffix="cm" value={form[f.key]} onChange={(v) => setV(f.key, v)} />
          ))}
        </div>
      </div>

      <div>
        <Label className="label-tiny">Note</Label>
        <Input value={form.notes} onChange={(e) => setV("notes", e.target.value)} />
      </div>

      <Button data-testid="save-measurement-btn" type="submit" className="w-full rounded-full bg-[color:var(--accent)] text-white hover:bg-[color:var(--accent-hover)] hover:text-white">
        {editing ? "Aggiorna rilievo" : "Salva rilievo"}
      </Button>
    </form>
  );
}
